/**
 * Utilitaires pour l'affichage et le formatage des évaluations
 * Centralise toutes les fonctions liées aux évaluations pour éviter la duplication
 */

export type EvaluationValue = 'Excellent' | 'Good' | 'Moyen' | 'Repeat' | 'Absent' | 'NotDone';

export interface EvalBadgeStyle {
  color: string;
  backgroundColor: string;
}

/**
 * Convertit une valeur d'évaluation anglaise en arabe
 * @param evalValue - Valeur d'évaluation en anglais
 * @returns Valeur d'évaluation en arabe
 */
export const mapEvalToArabic = (evalValue: string | undefined | null): string => {
  if (!evalValue || evalValue === 'NotDone') {
    return 'لم يتم';
  }
  
  const mapping: Record<string, string> = {
    'Excellent': 'ممتاز',
    'Good': 'جيد',
    'Moyen': 'متوسط',
    'Repeat': 'إعادة',
    'Absent': 'غائب',
    'NotDone': 'لم يتم'
  };
  
  return mapping[evalValue] || 'لم يتم';
};

/**
 * Retourne le style CSS pour un badge d'évaluation
 * @param evalValue - Valeur d'évaluation
 * @returns Style CSS pour le badge
 */
export const getEvalBadgeStyle = (evalValue: string | undefined | null): EvalBadgeStyle => {
  if (!evalValue || evalValue === 'NotDone' || evalValue === 'Nothing') {
    return {
      color: '#6b7280',
      backgroundColor: '#f3f4f6'
    };
  }
  
  const styles: Record<string, EvalBadgeStyle> = {
    'Excellent': {
      color: '#166534',
      backgroundColor: '#dcfce7'
    },
    'Good': {
      color: '#1e40af',
      backgroundColor: '#dbeafe'
    },
    'Moyen': {
      color: '#c2410c',
      backgroundColor: '#ffedd5'
    },
    'Repeat': {
      color: '#dc2626',
      backgroundColor: '#fee2e2'
    },
    'Absent': {
      color: '#dc2626',
      backgroundColor: '#fee2e2'
    },
    'NotDone': {
      color: '#7c3aed',
      backgroundColor: '#ede9fe'
    }
  };
  
  return styles[evalValue] || {
    color: '#6b7280',
    backgroundColor: '#f3f4f6'
  };
};

/**
 * Valide une valeur d'évaluation
 * @param value - Valeur à valider
 * @returns true si la valeur est valide
 */
export const isValidEvaluationValue = (value: string | null | undefined): value is EvaluationValue => {
  const validValues: EvaluationValue[] = ['Excellent', 'Good', 'Moyen', 'Repeat', 'Absent', 'NotDone'];
  return value !== null && value !== undefined && validValues.includes(value as EvaluationValue);
};

